import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Target, Timer, Trophy, RotateCcw, Check, X as XIcon, Play } from "lucide-react";
import { getQuizWords } from "@/lib/api";
import { Ornament } from "@/components/Ornament";

const QUESTION_COUNT = 10;
const SECONDS = 10;
const POINTS = 10;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Quiz() {
  const [words, setWords] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [phase, setPhase] = useState("idle"); // idle | playing | done
  const [qi, setQi] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState(null);
  const [timeLeft, setTimeLeft] = useState(SECONDS);
  const timerRef = useRef(null);

  useEffect(() => {
    getQuizWords().then(setWords).catch(() => {});
  }, []);

  const build = useCallback(() => {
    const pool = shuffle(words).slice(0, Math.min(QUESTION_COUNT, words.length));
    const qs = pool.map((w) => {
      const distractors = shuffle(
        words.filter((x) => x.id !== w.id && x.meaning !== w.meaning)
      ).slice(0, 3);
      return { word: w, options: shuffle([w, ...distractors]) };
    });
    setQuestions(qs);
  }, [words]);

  const start = () => {
    build();
    setQi(0);
    setScore(0);
    setSelected(null);
    setTimeLeft(SECONDS);
    setPhase("playing");
  };

  const advance = useCallback(() => {
    setSelected(null);
    setTimeLeft(SECONDS);
    setQi((i) => {
      if (i + 1 >= questions.length) {
        setPhase("done");
        return i;
      }
      return i + 1;
    });
  }, [questions.length]);

  const answer = (opt) => {
    if (selected) return;
    clearInterval(timerRef.current);
    setSelected(opt.id);
    if (opt.id === questions[qi].word.id) setScore((s) => s + POINTS);
    setTimeout(advance, 1200);
  };

  // Per-question countdown
  useEffect(() => {
    if (phase !== "playing" || selected) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          setSelected("__timeout__");
          setTimeout(advance, 900);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, qi, selected, advance]);

  const q = questions[qi];
  const maxScore = questions.length * POINTS;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center max-w-2xl mx-auto">
        <span className="eyebrow">Kelime Avı</span>
        <h1 className="mt-3 font-[Cinzel] text-4xl sm:text-5xl font-bold text-foreground">Söz Yarışı</h1>
        <p className="mt-4 text-foreground/75">
          Her soru için <strong>{SECONDS} saniye</strong>. Anlama bakıp doğru Eski Türkçe kelimeyi seçin.
          {" "}{QUESTION_COUNT} soru × {POINTS} puan = {QUESTION_COUNT * POINTS} puan.
        </p>
        <div className="flex justify-center text-accent mt-6">
          <Ornament className="w-40 h-5" />
        </div>
      </div>

      <div className="mt-10">
        {phase === "idle" && (
          <div className="folio-frame rounded-lg bg-card p-10 text-center" data-testid="quiz-start-screen">
            <span className="flex items-center justify-center w-16 h-16 rounded-full border border-accent/50 text-accent mx-auto mb-5">
              <Target className="w-7 h-7" />
            </span>
            <h2 className="font-[Cinzel] text-2xl font-semibold text-foreground">Hazır mısınız?</h2>
            <p className="mt-3 text-foreground/70">
              {words.length ? `${words.length} kelimelik hazineden sorular geliyor.` : "Kelimeler yükleniyor…"}
            </p>
            <button
              onClick={start}
              disabled={!words.length}
              data-testid="quiz-start-btn"
              className="mt-7 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              <Play className="w-4 h-4" /> Avı Başlat
            </button>
          </div>
        )}

        {phase === "playing" && q && (
          <div data-testid="quiz-play-screen">
            <div className="flex items-center justify-between text-sm mb-3">
              <span className="font-mono text-muted-foreground">
                Soru {qi + 1} / {questions.length}
              </span>
              <span className="font-mono rubric font-semibold" data-testid="quiz-score">
                Puan: {score} / {maxScore}
              </span>
            </div>

            {/* Timer bar */}
            <div className="h-2 rounded-full bg-secondary overflow-hidden mb-6">
              <motion.div
                key={qi}
                className="h-full bg-accent"
                initial={{ width: "100%" }}
                animate={{ width: `${(timeLeft / SECONDS) * 100}%` }}
                transition={{ duration: 0.5, ease: "linear" }}
              />
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={qi}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.3 }}
                className="folio-frame rounded-lg bg-card p-7 sm:p-9"
              >
                <div className="flex items-center justify-between">
                  <span className="eyebrow">Bu anlam hangi kelime?</span>
                  <span className="flex items-center gap-1.5 font-mono text-sm text-primary">
                    <Timer className="w-4 h-4" /> {timeLeft}s
                  </span>
                </div>
                <p className="mt-4 text-2xl sm:text-3xl font-[Cinzel] text-foreground">
                  “{q.word.meaning}”
                </p>

                <div className="mt-7 grid sm:grid-cols-2 gap-3">
                  {q.options.map((opt) => {
                    const isCorrect = opt.id === q.word.id;
                    const isPicked = selected === opt.id;
                    const reveal = selected !== null;
                    let cls = "border-border bg-card hover:border-accent";
                    if (reveal && isCorrect) cls = "border-green-700 bg-green-700/10";
                    else if (reveal && isPicked && !isCorrect) cls = "border-primary bg-primary/10";
                    return (
                      <button
                        key={opt.id}
                        onClick={() => answer(opt)}
                        disabled={reveal}
                        data-testid={`quiz-option-${opt.id}`}
                        className={`card-hover flex items-center justify-between rounded-lg border px-5 py-4 text-left transition-colors ${cls}`}
                      >
                        <span className="font-[Cinzel] text-lg text-foreground">{opt.word}</span>
                        {reveal && isCorrect && <Check className="w-5 h-5 text-green-700" />}
                        {reveal && isPicked && !isCorrect && <XIcon className="w-5 h-5 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {phase === "done" && (
          <div className="folio-frame rounded-lg bg-card p-10 text-center" data-testid="quiz-done-screen">
            <span className="flex items-center justify-center w-16 h-16 rounded-full border border-accent/50 text-accent mx-auto mb-5">
              <Trophy className="w-7 h-7" />
            </span>
            <h2 className="font-[Cinzel] text-2xl font-semibold text-foreground">Av tamamlandı!</h2>
            <p className="mt-3 text-5xl font-[Cinzel] font-bold rubric" data-testid="quiz-final-score">
              {score}
              <span className="text-2xl text-muted-foreground"> / {maxScore}</span>
            </p>
            <p className="mt-3 text-foreground/70">
              {score === maxScore
                ? "Bilge gibisiniz — tam isabet!"
                : score >= maxScore * 0.6
                ? "Alp yürekli! Güzel bir skor."
                : "Dîvân sizi bekliyor; biraz daha keşfedin."}
            </p>
            <button
              onClick={start}
              data-testid="quiz-restart-btn"
              className="mt-7 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Tekrar Oyna
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
